import * as React from 'react';
import { useEffect, useMemo } from 'react';
import { useCustomerStore } from '../../../redux/customers';
import { useOrderStore } from '../../../redux/orders';
import { useToastStore } from '../../../redux/toast';
import { useAppSelector } from '../../../redux/hooks';
import { orderByUuidSelectorFactory } from '../../../redux/orders/selectors';
import { OrderDetailPage } from '../components';

interface Props {
    uuid: string;
}

export const OrderDetailPageContainer: React.FC<Props> = ({ uuid }) => {
    const toastStore = useToastStore();
    const { selectedId: customerId, customers } = useCustomerStore();
    const { isFetching, fetchOrders, errorMessage, clearErrors } = useOrderStore();

    const orderSelector = useMemo(() => orderByUuidSelectorFactory(uuid), [uuid]);
    const order = useAppSelector(orderSelector);

    // Banking must come from the order's OWN customer, never the active one. A
    // multi-customer user who switches active customer and then reaches another
    // customer's order (back/bookmark/restored route) would otherwise see the active
    // customer's bank account paired with this order's invoice reference and pay the
    // wrong company (F30). Resolve the owner by the order's customerId; if that customer
    // isn't in the store, render no banking rather than a mismatched one.
    const orderCustomer = useMemo(
        () => (order ? customers.find(c => c.id === order.customerId) ?? null : null),
        [customers, order]
    );

    useEffect(() => {
        if (!order && customerId) {
            fetchOrders({ customerId }).catch(() => { /* handled via errorMessage */ });
        }
    }, [uuid, customerId]);

    useEffect(() => {
        if (errorMessage) {
            toastStore.showError({ message: errorMessage }).then(() => clearErrors());
        }
    }, [errorMessage]);

    return (
        <OrderDetailPage
            order={order}
            isFetching={isFetching}
            customer={orderCustomer}
        />
    );
};
